import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "../state/rootReducer";

export interface OrgSwitcherOrganization {
  _id: string;
  organization_name?: string;
  organizational_code?: string;
  logoUrl?: string;
  backgroundUrl?: string;
}

const selectAuthOrganizationInfo = (state: RootState) => state.auth.organization_info;
const selectAuthManagedOrgs = (state: RootState) => state.auth.managed_orgs;
const selectAuthMemberOrgs = (state: RootState) => state.auth.member_orgs;

/** Memoized so OrgSwitcher does not re-render on unrelated store updates. */
export const selectOrgSwitcherOrganizations = createSelector(
  [selectAuthOrganizationInfo, selectAuthManagedOrgs, selectAuthMemberOrgs],
  (organizationInfo, managedOrgs, memberOrgs): OrgSwitcherOrganization[] => {
    const byId = new Map<string, OrgSwitcherOrganization>();
    const mergedManagedOrgs = managedOrgs || [];
    const mergedMemberOrgs = memberOrgs || [];

    // Merge all three sources; Map deduplicates by _id automatically.
    // Order: currentOrg first, then managed, then member (non-manager org memberships).
    [organizationInfo, ...mergedManagedOrgs, ...mergedMemberOrgs].forEach((org) => {
      const orgId = org?._id?.toString();
      if (!orgId) return;
      byId.set(orgId, { ...(byId.get(orgId) || {}), ...org, _id: orgId });
    });

    return Array.from(byId.values()).sort((a, b) =>
      (a.organization_name || "").localeCompare(b.organization_name || "")
    );
  }
);
